import * as Effect from "effect/Effect";
import { identity } from "effect/Function";
import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";
import * as Struct from "effect/Struct";
import { ProviderOptionSelections } from "./model.js";
import { CommandId, EventId, IsoDateTime, MessageId, NonNegativeInt, ProjectId, ProviderItemId, ThreadId, TrimmedNonEmptyString, TurnId, } from "./baseSchemas.js";
import { ProviderInstanceId } from "./providerInstance.js";
import { InferenceFailureKind } from "./providerRuntime.js";
import { PublicActivity } from "./activityLabel.js";
export const ORCHESTRATION_WS_METHODS = {
    dispatchCommand: "orchestration.dispatchCommand",
    replayEvents: "orchestration.replayEvents",
    getArchivedShellSnapshot: "orchestration.getArchivedShellSnapshot",
    subscribeShell: "orchestration.subscribeShell",
    subscribeThread: "orchestration.subscribeThread",
    fetchOlderThreadMessages: "orchestration.fetchOlderThreadMessages",
    fetchOlderThreadActivities: "orchestration.fetchOlderThreadActivities",
    exportThreadDebugLog: "orchestration.exportThreadDebugLog",
};
/**
 * `ModelSelection` — selection of a model on a configured provider instance.
 *
 * The routing key is `instanceId` (a user-defined slug identifying one
 * configured provider instance). Drivers, credentials, working-directory
 * bindings, and any other per-instance state are recovered from the
 * runtime registry via the instance id.
 *
 * Wire legacy: persisted selections produced before the driver/instance
 * split carried a `provider: <driver-id>` field instead. The schema absorbs
 * that shape via a pre-decoding transform — `{provider, model}` is promoted
 * to `{instanceId: defaultInstanceIdForDriver(provider), model}`. No
 * post-decode compatibility code lives in the runtime; the transform is the
 * only compat surface.
 */
const ModelSelectionWire = Schema.Struct({
    instanceId: ProviderInstanceId,
    model: TrimmedNonEmptyString,
    options: Schema.optionalKey(ProviderOptionSelections),
});
// Source shape for persisted legacy payloads. Fields are typed as
// `Schema.Unknown` so malformed drafts still make it into the transform and
// fail validation through the target schema (with proper error messages)
// rather than at the source-struct layer where the error is less actionable.
const ModelSelectionSource = Schema.Struct({
    provider: Schema.optional(Schema.Unknown),
    instanceId: Schema.optional(Schema.Unknown),
    model: Schema.Unknown,
    options: Schema.optional(Schema.Unknown),
});
export const ModelSelection = ModelSelectionSource.pipe(Schema.decodeTo(ModelSelectionWire, SchemaTransformation.transformOrFail({
    decode: (raw) => {
        // Resolve the routing key: prefer an explicit `instanceId`; fall
        // back to promoting the legacy `provider` slug (the canonical
        // `defaultInstanceIdForDriver` mapping) so persisted rollout-era
        // payloads decode without data loss. The target schema brands the
        // string as `ProviderInstanceId`.
        const instanceIdSource = raw.instanceId !== undefined
            ? raw.instanceId
            : typeof raw.provider === "string"
                ? raw.provider
                : undefined;
        const base = {
            instanceId: instanceIdSource,
            model: raw.model,
        };
        if (raw.options !== undefined)
            base.options = raw.options;
        return Effect.succeed(base);
    },
    encode: (value) => {
        const base = {
            model: value.model,
            instanceId: value.instanceId,
        };
        if (value.options !== undefined)
            base.options = value.options;
        return Effect.succeed(base);
    },
})));
export const PROVIDER_SEND_TURN_MAX_INPUT_CHARS = 120_000;
export const PROVIDER_SEND_TURN_MAX_ATTACHMENTS = 50;
export const PROVIDER_SEND_TURN_MAX_IMAGE_BYTES = 20 * 1024 * 1024;
// Every image in a turn is base64-inlined into one request to the inference
// proxy, so the aggregate - not the per-file size - is what the proxy's body
// limit sees.
export const PROVIDER_SEND_TURN_MAX_TOTAL_IMAGE_BYTES = 20 * 1024 * 1024;
export const PROVIDER_SEND_TURN_MAX_FILE_BYTES = 1024 * 1024 * 1024;
// Ids are "<threadSegment>/<fileName>": thread segment up to 80 chars, file name
// up to 255, plus the separator and a possible "-N" collision suffix.
const CHAT_ATTACHMENT_ID_MAX_CHARS = 384;
// Correlation id is command id by design in this model.
export const CorrelationId = CommandId;
// New attachment ids are thread-relative paths "<threadId>/<fileName>" addressing
// <workspaceRoot>/attachments/<threadId>/<fileName>. The trailing "/<fileName>" is
// optional so legacy flat ids ("<threadSegment>-<uuid>") persisted before the
// per-thread layout still decode on read (their files no longer resolve, but the
// thread stays loadable). Traversal is forbidden in either form; on-disk
// resolution applies defense-in-depth.
const CHAT_ATTACHMENT_ID_PATTERN = new RegExp(String.raw `^(?!\.\.?(?:/|$))[a-z0-9._-]+(?:/(?!\.\.?$)[^\x00-\x1f<>:"\\/|?*]+)?$`, "i");
const ChatAttachmentId = TrimmedNonEmptyString.check(Schema.isMaxLength(CHAT_ATTACHMENT_ID_MAX_CHARS), Schema.isPattern(CHAT_ATTACHMENT_ID_PATTERN));
export const ChatImageAttachment = Schema.Struct({
    type: Schema.Literal("image"),
    id: ChatAttachmentId,
    name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
    mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
    sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
});
export const ChatFileAttachment = Schema.Struct({
    type: Schema.Literal("file"),
    id: ChatAttachmentId,
    name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
    mimeType: Schema.String.check(Schema.isMaxLength(100)),
    sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_FILE_BYTES)),
});
export const ChatAttachment = Schema.Union([ChatImageAttachment, ChatFileAttachment]);
/**
 * The account that created a project or a thread, as its server token named
 * it. On a team server several members share the projects and threads; this
 * is who started one. A subagent's thread carries the owner of its parent.
 */
export const ProjectCreator = Schema.Struct({
    userId: TrimmedNonEmptyString,
    name: Schema.NullOr(TrimmedNonEmptyString),
});
export const OrchestrationProject = Schema.Struct({
    id: ProjectId,
    title: TrimmedNonEmptyString,
    workspaceRoot: TrimmedNonEmptyString,
    defaultModelSelection: Schema.NullOr(ModelSelection),
    /** Absent on a project created before creators were recorded. */
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    deletedAt: Schema.NullOr(IsoDateTime),
    archivedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
});
export const OrchestrationMessageRole = Schema.Literals(["user", "assistant", "system"]);
/** Codex's own phase of an assistant message: thinking aloud between steps, or the answer. */
export const OrchestrationMessagePhase = Schema.Literals([
    "commentary",
    "final_answer",
    "question",
]);
export const OrchestrationMessage = Schema.Struct({
    id: MessageId,
    role: OrchestrationMessageRole,
    text: Schema.String,
    attachments: Schema.optional(Schema.Array(ChatAttachment)),
    turnId: Schema.NullOr(TurnId),
    streaming: Schema.Boolean,
    phase: Schema.optionalKey(OrchestrationMessagePhase),
    /**
     * Monotonic orchestration-event sequence of the message's first
     * `thread.message-sent` event. Server-assigned; used as the canonical
     * ordering key so message order is immune to client clock skew.
     * Optional for backward compatibility with rows projected before
     * migration 031.
     */
    sequence: Schema.optional(NonNegativeInt),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const OrchestrationSessionStatus = Schema.Literals([
    "idle",
    "starting",
    "running",
    "ready",
    "interrupted",
    "stopped",
    "error",
]);
export const OrchestrationSession = Schema.Struct({
    threadId: ThreadId,
    status: OrchestrationSessionStatus,
    providerName: Schema.NullOr(TrimmedNonEmptyString),
    providerInstanceId: Schema.optional(ProviderInstanceId),
    activeTurnId: Schema.NullOr(TurnId),
    lastError: Schema.NullOr(TrimmedNonEmptyString),
    updatedAt: IsoDateTime,
});
export const TurnFailureReason = Schema.Literals([
    ...InferenceFailureKind.literals,
    "storage_full",
    "agent_failed",
]);
export const TurnFailurePayload = Schema.Struct({ reason: TurnFailureReason });
export const PUBLIC_SESSION_ERROR = "The agent could not complete the request.";
export const OrchestrationThreadActivityTone = Schema.Literals([
    "info",
    "tool",
    "approval",
    "error",
]);
export const OrchestrationThreadActivity = Schema.Struct({
    id: EventId,
    tone: OrchestrationThreadActivityTone,
    kind: TrimmedNonEmptyString,
    summary: TrimmedNonEmptyString,
    /** Activity data; presentation selects the fields sent to clients. */
    payload: Schema.optional(Schema.Unknown),
    /** Audience-safe label the server computes from the activity label policy. */
    public: Schema.optionalKey(PublicActivity),
    turnId: Schema.NullOr(TurnId),
    sequence: Schema.optional(NonNegativeInt),
    createdAt: IsoDateTime,
});
export const TurnFailedActivity = Schema.Struct({
    ...OrchestrationThreadActivity.fields,
    kind: Schema.Literal("turn.failed"),
    payload: TurnFailurePayload,
});
const MODEL_IDENTITY_KEYS = new Set([
    "model",
    "fromModel",
    "toModel",
    "modelUsage",
    "modelProvider",
    "reasoningEffort",
    "reasoning_effort",
    "modelReasoningEffort",
]);
function omitModelIdentity(value) {
    if (Array.isArray(value)) {
        let next = null;
        value.forEach((entry, index) => {
            const cleaned = omitModelIdentity(entry);
            if (cleaned !== entry) {
                next ??= [...value];
                next[index] = cleaned;
            }
        });
        return next ?? value;
    }
    if (value === null || typeof value !== "object")
        return value;
    let next = null;
    for (const [key, entry] of Object.entries(value)) {
        if (MODEL_IDENTITY_KEYS.has(key)) {
            next ??= { ...value };
            delete next[key];
            continue;
        }
        const cleaned = omitModelIdentity(entry);
        if (cleaned !== entry) {
            next ??= { ...value };
            next[key] = cleaned;
        }
    }
    return next ?? value;
}
/** Activity payload as clients receive it: encoding drops every model identity key. */
const ClientActivityPayload = Schema.Unknown.pipe(Schema.decodeTo(Schema.Unknown, SchemaTransformation.transform({ decode: identity, encode: omitModelIdentity })));
export const ClientOrchestrationThreadActivity = OrchestrationThreadActivity.mapFields(Struct.assign({ payload: Schema.optional(ClientActivityPayload) }));
export const ClientOrchestrationSession = OrchestrationSession.mapFields(Struct.omit(["providerName", "providerInstanceId"]));
const OrchestrationLatestTurnState = Schema.Literals([
    "running",
    "interrupted",
    "completed",
    "error",
]);
export const OrchestrationLatestTurn = Schema.Struct({
    turnId: TurnId,
    state: OrchestrationLatestTurnState,
    requestedAt: IsoDateTime,
    startedAt: Schema.NullOr(IsoDateTime),
    completedAt: Schema.NullOr(IsoDateTime),
    assistantMessageId: Schema.NullOr(MessageId),
});
export const ThreadKind = Schema.Literals(["root", "subagent"]);
export const DEFAULT_THREAD_KIND = "root";
export const OrchestrationThread = Schema.Struct({
    id: ThreadId,
    projectId: ProjectId,
    title: TrimmedNonEmptyString,
    modelSelection: ModelSelection,
    threadKind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_THREAD_KIND))),
    parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    latestTurn: Schema.NullOr(OrchestrationLatestTurn),
    /** Absent on a thread created before owners were recorded. */
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    deletedAt: Schema.NullOr(IsoDateTime),
    messages: Schema.Array(OrchestrationMessage),
    activities: Schema.Array(OrchestrationThreadActivity),
    session: Schema.NullOr(OrchestrationSession),
});
export const OrchestrationReadModel = Schema.Struct({
    snapshotSequence: NonNegativeInt,
    projects: Schema.Array(OrchestrationProject),
    threads: Schema.Array(OrchestrationThread),
    updatedAt: IsoDateTime,
});
export const OrchestrationProjectShell = Schema.Struct({
    id: ProjectId,
    title: TrimmedNonEmptyString,
    workspaceRoot: TrimmedNonEmptyString,
    defaultModelSelection: Schema.NullOr(ModelSelection),
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    archivedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
});
export const OrchestrationThreadShell = Schema.Struct({
    id: ThreadId,
    projectId: ProjectId,
    title: TrimmedNonEmptyString,
    modelSelection: ModelSelection,
    threadKind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_THREAD_KIND))),
    parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    latestTurn: Schema.NullOr(OrchestrationLatestTurn),
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    session: Schema.NullOr(OrchestrationSession),
    latestUserMessageAt: Schema.NullOr(IsoDateTime),
});
export const OrchestrationShellSnapshot = Schema.Struct({
    snapshotSequence: NonNegativeInt,
    projects: Schema.Array(OrchestrationProjectShell),
    threads: Schema.Array(OrchestrationThreadShell),
    updatedAt: IsoDateTime,
});
export const ClientOrchestrationProject = OrchestrationProject.mapFields(Struct.omit(["defaultModelSelection"]));
export const ClientOrchestrationThread = OrchestrationThread.mapFields((fields) => ({
    ...Struct.omit(fields, ["modelSelection"]),
    activities: Schema.Array(ClientOrchestrationThreadActivity),
    session: Schema.NullOr(ClientOrchestrationSession),
}));
export const ClientOrchestrationReadModel = OrchestrationReadModel.mapFields(Struct.assign({
    projects: Schema.Array(ClientOrchestrationProject),
    threads: Schema.Array(ClientOrchestrationThread),
}));
export const ClientOrchestrationProjectShell = OrchestrationProjectShell.mapFields(Struct.omit(["defaultModelSelection"]));
export const ClientOrchestrationThreadShell = OrchestrationThreadShell.mapFields((fields) => ({
    ...Struct.omit(fields, ["modelSelection"]),
    session: Schema.NullOr(ClientOrchestrationSession),
}));
export const ClientOrchestrationShellSnapshot = OrchestrationShellSnapshot.mapFields(Struct.assign({
    projects: Schema.Array(ClientOrchestrationProjectShell),
    threads: Schema.Array(ClientOrchestrationThreadShell),
}));
export const ClientOrchestrationShellStreamEvent = Schema.Union([
    Schema.Struct({
        kind: Schema.Literal("project-upserted"),
        sequence: NonNegativeInt,
        project: ClientOrchestrationProjectShell,
    }),
    Schema.Struct({
        kind: Schema.Literal("project-removed"),
        sequence: NonNegativeInt,
        projectId: ProjectId,
    }),
    Schema.Struct({
        kind: Schema.Literal("thread-upserted"),
        sequence: NonNegativeInt,
        thread: ClientOrchestrationThreadShell,
    }),
    Schema.Struct({
        kind: Schema.Literal("thread-removed"),
        sequence: NonNegativeInt,
        threadId: ThreadId,
    }),
]);
export const ClientOrchestrationShellStreamItem = Schema.Union([
    Schema.Struct({
        kind: Schema.Literal("snapshot"),
        snapshot: ClientOrchestrationShellSnapshot,
    }),
    ClientOrchestrationShellStreamEvent,
    Schema.Struct({ kind: Schema.Literal("resync") }),
]);
export const OrchestrationSubscribeThreadInput = Schema.Struct({
    threadId: ThreadId,
});
export const MessageCursor = Schema.Struct({
    /**
     * Canonical `sequence` of the cursor message (server-assigned, monotonic,
     * clock-skew immune — see {@link OrchestrationMessage.sequence}). This is the
     * primary keyset ordering key; `createdAt`/`messageId` only break ties between
     * rows that share a `sequence` (i.e. legacy rows projected before the sequence
     * backfill, whose `sequence` is `null`). Keeping it in the cursor lets the
     * older-page query page in the same order as the full detail view instead of
     * by the (skewable) wall-clock `createdAt`.
     */
    sequence: Schema.NullOr(NonNegativeInt),
    createdAt: IsoDateTime,
    messageId: MessageId,
});
export const OrchestrationThreadMessageWindow = Schema.Struct({
    hasMoreBefore: Schema.Boolean,
    oldestCursor: Schema.NullOr(MessageCursor),
});
export const ActivityCursor = Schema.Struct({
    sequence: Schema.NullOr(NonNegativeInt),
    createdAt: IsoDateTime,
    activityId: EventId,
});
export const OrchestrationThreadActivityWindow = Schema.Struct({
    hasMoreBefore: Schema.Boolean,
    oldestCursor: Schema.NullOr(ActivityCursor),
});
/**
 * Text of an assistant message that is still being generated.
 *
 * Live text is transport-only: it lives in server memory, never in the event
 * log, and is superseded by the `thread.message-sent` event that closes the
 * message. `text` is exactly what an already-attached subscriber holds, so
 * {@link OrchestrationLiveMessageFrame} extends it without a gap.
 */
export const OrchestrationLiveMessage = Schema.Struct({
    messageId: MessageId,
    turnId: Schema.NullOr(TurnId),
    text: Schema.String,
    /** Server clock, so a provisional message sorts like the durable one will. */
    createdAt: IsoDateTime,
});
/**
 * An increment of live assistant text.
 *
 * `emittedLength` is the total live length after applying `delta`, so a
 * subscriber can position the delta against the text it already holds and
 * append only the unseen tail. Frames are produced per subscription from the
 * server's authoritative text, so they never skip ahead of what that
 * subscriber received; a frame that still appears to (state lost across a
 * reconnect) is ignored, and the next snapshot or the closing message event
 * carries the authoritative full text.
 */
export const OrchestrationLiveMessageFrame = Schema.Struct({
    threadId: ThreadId,
    messageId: MessageId,
    turnId: Schema.NullOr(TurnId),
    delta: Schema.String,
    emittedLength: NonNegativeInt,
    /** Server clock, so a provisional message sorts like the durable one will. */
    createdAt: IsoDateTime,
});
export const UserMessageRailEntry = Schema.Struct({
    messageId: MessageId,
    title: Schema.String,
    answer: Schema.NullOr(Schema.String),
});
export const UserMessageRailUpdate = Schema.Struct({
    replace: Schema.Boolean,
    entries: Schema.Array(UserMessageRailEntry),
});
export const ClientOrchestrationThreadDetailSnapshot = Schema.Struct({
    snapshotSequence: NonNegativeInt,
    thread: ClientOrchestrationThread,
    messageWindow: OrchestrationThreadMessageWindow,
    activityWindow: OrchestrationThreadActivityWindow,
    /** In-flight assistant text held in server memory; see {@link OrchestrationLiveMessage}. */
    liveMessages: Schema.Array(OrchestrationLiveMessage),
});
export const ProjectCreateCommand = Schema.Struct({
    type: Schema.Literal("project.create"),
    commandId: CommandId,
    projectId: ProjectId,
    title: TrimmedNonEmptyString,
    workspaceRoot: TrimmedNonEmptyString,
    createWorkspaceRootIfMissing: Schema.optional(Schema.Boolean),
    defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
    /** Stamped by the server from the dispatching connection's token; a client leaves it unset. */
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
});
const ProjectMetaUpdateCommand = Schema.Struct({
    type: Schema.Literal("project.meta.update"),
    commandId: CommandId,
    projectId: ProjectId,
    title: Schema.optional(TrimmedNonEmptyString),
    workspaceRoot: Schema.optional(TrimmedNonEmptyString),
    defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
});
const ProjectDeleteCommand = Schema.Struct({
    type: Schema.Literal("project.delete"),
    commandId: CommandId,
    projectId: ProjectId,
    force: Schema.optional(Schema.Boolean),
    deleteFiles: Schema.optional(Schema.Boolean),
    actorUserId: Schema.optional(TrimmedNonEmptyString),
});
const ProjectArchiveCommand = Schema.Struct({
    type: Schema.Literal("project.archive"),
    commandId: CommandId,
    projectId: ProjectId,
    actorUserId: Schema.optional(TrimmedNonEmptyString),
});
const ProjectUnarchiveCommand = Schema.Struct({
    type: Schema.Literal("project.unarchive"),
    commandId: CommandId,
    projectId: ProjectId,
    actorUserId: Schema.optional(TrimmedNonEmptyString),
});
const ThreadCreateCommand = Schema.Struct({
    type: Schema.Literal("thread.create"),
    commandId: CommandId,
    threadId: ThreadId,
    projectId: ProjectId,
    title: TrimmedNonEmptyString,
    modelSelection: ModelSelection,
    threadKind: Schema.optional(ThreadKind),
    parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
    /** Stamped by the server from the dispatching connection's token; a client leaves it unset. */
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
});
const ThreadDeleteCommand = Schema.Struct({
    type: Schema.Literal("thread.delete"),
    commandId: CommandId,
    threadId: ThreadId,
});
const ThreadArchiveCommand = Schema.Struct({
    type: Schema.Literal("thread.archive"),
    commandId: CommandId,
    threadId: ThreadId,
});
const ThreadUnarchiveCommand = Schema.Struct({
    type: Schema.Literal("thread.unarchive"),
    commandId: CommandId,
    threadId: ThreadId,
});
const ThreadMetaUpdateCommand = Schema.Struct({
    type: Schema.Literal("thread.meta.update"),
    commandId: CommandId,
    threadId: ThreadId,
    title: Schema.optional(TrimmedNonEmptyString),
    modelSelection: Schema.optional(ModelSelection),
});
const ThreadTurnStartBootstrapCreateThread = Schema.Struct({
    projectId: ProjectId,
    title: TrimmedNonEmptyString,
    modelSelection: ModelSelection,
    /** Stamped by the server from the dispatching connection's token; a client leaves it unset. */
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
});
const ThreadTurnStartBootstrap = Schema.Struct({
    createThread: Schema.optional(ThreadTurnStartBootstrapCreateThread),
});
export const ThreadTurnStartCommand = Schema.Struct({
    type: Schema.Literal("thread.turn.start"),
    commandId: CommandId,
    threadId: ThreadId,
    message: Schema.Struct({
        messageId: MessageId,
        role: Schema.Literal("user"),
        text: Schema.String,
        attachments: Schema.Array(ChatAttachment),
    }),
    modelSelection: Schema.optional(ModelSelection),
    titleSeed: Schema.optional(TrimmedNonEmptyString),
    bootstrap: Schema.optional(ThreadTurnStartBootstrap),
    /**
     * The user whose connection sent the message, whose inference credential the
     * turn runs on. Stamped by the server from the dispatching connection's
     * token; a client leaves it unset.
     */
    authorUserId: Schema.optional(TrimmedNonEmptyString),
});
const ClientProjectCreateCommand = ProjectCreateCommand.mapFields(Struct.omit(["defaultModelSelection", "createdBy"]));
const ClientProjectMetaUpdateCommand = ProjectMetaUpdateCommand.mapFields(Struct.omit(["defaultModelSelection"]));
const ClientThreadCreateCommand = ThreadCreateCommand.mapFields(Struct.omit(["modelSelection", "createdBy"]));
const ClientThreadMetaUpdateCommand = ThreadMetaUpdateCommand.mapFields(Struct.omit(["modelSelection"]));
const ClientThreadTurnStartBootstrap = Schema.Struct({
    createThread: Schema.optional(ThreadTurnStartBootstrapCreateThread.mapFields(Struct.omit(["modelSelection", "createdBy"]))),
});
const ClientThreadTurnStartCommand = ThreadTurnStartCommand.mapFields((fields) => ({
    ...Struct.omit(fields, ["modelSelection", "authorUserId"]),
    deepWork: Schema.optional(Schema.Boolean),
    bootstrap: Schema.optional(ClientThreadTurnStartBootstrap),
}));
const ThreadTurnInterruptCommand = Schema.Struct({
    type: Schema.Literal("thread.turn.interrupt"),
    commandId: CommandId,
    threadId: ThreadId,
    turnId: Schema.optional(TurnId),
});
const ThreadSessionStopCommand = Schema.Struct({
    type: Schema.Literal("thread.session.stop"),
    commandId: CommandId,
    threadId: ThreadId,
});
const DispatchableClientOrchestrationCommand = Schema.Union([
    ProjectCreateCommand,
    ProjectMetaUpdateCommand,
    ProjectDeleteCommand,
    ProjectArchiveCommand,
    ProjectUnarchiveCommand,
    ThreadCreateCommand,
    ThreadDeleteCommand,
    ThreadArchiveCommand,
    ThreadUnarchiveCommand,
    ThreadMetaUpdateCommand,
    ThreadTurnStartCommand,
    ThreadTurnInterruptCommand,
    ThreadSessionStopCommand,
]);
export const ClientOrchestrationCommand = Schema.Union([
    ClientProjectCreateCommand,
    ClientProjectMetaUpdateCommand,
    ProjectDeleteCommand.mapFields(Struct.omit(["actorUserId"])),
    ProjectArchiveCommand.mapFields(Struct.omit(["actorUserId"])),
    ProjectUnarchiveCommand.mapFields(Struct.omit(["actorUserId"])),
    ClientThreadCreateCommand,
    ThreadDeleteCommand,
    ThreadArchiveCommand,
    ThreadUnarchiveCommand,
    ClientThreadMetaUpdateCommand,
    ClientThreadTurnStartCommand,
    ThreadTurnInterruptCommand,
    ThreadSessionStopCommand,
]);
const ThreadSessionSetCommand = Schema.Struct({
    type: Schema.Literal("thread.session.set"),
    commandId: CommandId,
    threadId: ThreadId,
    session: OrchestrationSession,
    createdAt: IsoDateTime,
});
/**
 * Opens an assistant message in the event log before its text exists.
 *
 * The text itself streams through memory and reaches the log only when the
 * message completes, but the message still has to take its place in the
 * timeline the moment it starts: ordering runs on the event sequence, and a
 * message that claims one only at completion sorts after everything that
 * happened while it was being written.
 */
const ThreadMessageAssistantOpenCommand = Schema.Struct({
    type: Schema.Literal("thread.message.assistant.open"),
    commandId: CommandId,
    threadId: ThreadId,
    messageId: MessageId,
    turnId: Schema.optional(TurnId),
});
const ThreadMessageAssistantDeltaCommand = Schema.Struct({
    type: Schema.Literal("thread.message.assistant.delta"),
    commandId: CommandId,
    threadId: ThreadId,
    messageId: MessageId,
    delta: Schema.String,
    turnId: Schema.optional(TurnId),
});
const ThreadMessageAssistantCompleteCommand = Schema.Struct({
    type: Schema.Literal("thread.message.assistant.complete"),
    commandId: CommandId,
    threadId: ThreadId,
    messageId: MessageId,
    turnId: Schema.optional(TurnId),
    phase: Schema.optional(OrchestrationMessagePhase),
});
const ThreadTurnSettleCommand = Schema.Struct({
    type: Schema.Literal("thread.turn.settle"),
    commandId: CommandId,
    threadId: ThreadId,
    turnId: TurnId,
    state: Schema.Literals(["completed", "interrupted", "error"]),
});
const ThreadActivityAppendCommand = Schema.Struct({
    type: Schema.Literal("thread.activity.append"),
    commandId: CommandId,
    threadId: ThreadId,
    activity: OrchestrationThreadActivity,
    createdAt: IsoDateTime,
});
const InternalOrchestrationCommand = Schema.Union([
    ThreadSessionSetCommand,
    ThreadTurnSettleCommand,
    ThreadMessageAssistantOpenCommand,
    ThreadMessageAssistantDeltaCommand,
    ThreadMessageAssistantCompleteCommand,
    ThreadActivityAppendCommand,
]);
export const OrchestrationCommand = Schema.Union([
    DispatchableClientOrchestrationCommand,
    InternalOrchestrationCommand,
]);
export const OrchestrationEventType = Schema.Literals([
    "project.created",
    "project.meta-updated",
    "project.deleted",
    "project.archived",
    "project.unarchived",
    "thread.created",
    "thread.deleted",
    "thread.archived",
    "thread.unarchived",
    "thread.meta-updated",
    "thread.runtime-mode-set",
    "thread.interaction-mode-set",
    "thread.message-sent",
    "thread.turn-start-requested",
    "thread.turn-interrupt-requested",
    "thread.turn-settled",
    "thread.approval-response-requested",
    "thread.user-input-response-requested",
    "thread.session-stop-requested",
    "thread.session-set",
    "thread.proposed-plan-upserted",
    "thread.activity-appended",
]);
export const OrchestrationAggregateKind = Schema.Literals(["project", "thread"]);
export const OrchestrationActorKind = Schema.Literals(["client", "server", "provider"]);
export const ProjectCreatedPayload = Schema.Struct({
    projectId: ProjectId,
    title: TrimmedNonEmptyString,
    workspaceRoot: TrimmedNonEmptyString,
    defaultModelSelection: Schema.NullOr(ModelSelection),
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const ProjectMetaUpdatedPayload = Schema.Struct({
    projectId: ProjectId,
    title: Schema.optional(TrimmedNonEmptyString),
    workspaceRoot: Schema.optional(TrimmedNonEmptyString),
    defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
    updatedAt: IsoDateTime,
});
export const ProjectDeletedPayload = Schema.Struct({
    projectId: ProjectId,
    deleteFiles: Schema.optional(Schema.Boolean),
    workspaceRoot: Schema.optional(TrimmedNonEmptyString),
    deletedAt: IsoDateTime,
});
export const ProjectArchivedPayload = Schema.Struct({
    projectId: ProjectId,
    archivedAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const ProjectUnarchivedPayload = Schema.Struct({
    projectId: ProjectId,
    updatedAt: IsoDateTime,
});
export const ThreadCreatedPayload = Schema.Struct({
    threadId: ThreadId,
    projectId: ProjectId,
    title: TrimmedNonEmptyString,
    modelSelection: ModelSelection,
    threadKind: ThreadKind.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_THREAD_KIND))),
    parentThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
    createdBy: Schema.optional(Schema.NullOr(ProjectCreator)),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const ThreadDeletedPayload = Schema.Struct({
    threadId: ThreadId,
    deletedAt: IsoDateTime,
});
export const ThreadArchivedPayload = Schema.Struct({
    threadId: ThreadId,
    archivedAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const ThreadUnarchivedPayload = Schema.Struct({
    threadId: ThreadId,
    updatedAt: IsoDateTime,
});
export const ThreadMetaUpdatedPayload = Schema.Struct({
    threadId: ThreadId,
    title: Schema.optional(TrimmedNonEmptyString),
    modelSelection: Schema.optional(ModelSelection),
    updatedAt: IsoDateTime,
});
/**
 * Payload of event types kept in the stored event log for decoding only
 * (runtime/interaction mode changes, proposed plans, approval and user-input
 * responses). Stored fields beyond `threadId` are dropped on decode.
 */
export const LegacyThreadEventPayload = Schema.Struct({
    threadId: ThreadId,
});
export const ThreadMessageSentPayload = Schema.Struct({
    threadId: ThreadId,
    messageId: MessageId,
    role: OrchestrationMessageRole,
    text: Schema.String,
    attachments: Schema.optional(Schema.Array(ChatAttachment)),
    turnId: Schema.NullOr(TurnId),
    streaming: Schema.Boolean,
    phase: Schema.optionalKey(OrchestrationMessagePhase),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const ThreadTurnStartRequestedPayload = Schema.Struct({
    threadId: ThreadId,
    messageId: MessageId,
    modelSelection: Schema.optional(ModelSelection),
    titleSeed: Schema.optional(TrimmedNonEmptyString),
    /** The command's `authorUserId`. */
    authorUserId: Schema.optional(TrimmedNonEmptyString),
    createdAt: IsoDateTime,
});
export const ThreadTurnInterruptRequestedPayload = Schema.Struct({
    threadId: ThreadId,
    turnId: Schema.optional(TurnId),
    createdAt: IsoDateTime,
});
export const ThreadTurnSettledPayload = Schema.Struct({
    threadId: ThreadId,
    turnId: TurnId,
    state: Schema.Literals(["completed", "interrupted", "error"]),
    createdAt: IsoDateTime,
});
export const ThreadSessionStopRequestedPayload = Schema.Struct({
    threadId: ThreadId,
    createdAt: IsoDateTime,
});
export const ThreadSessionSetPayload = Schema.Struct({
    threadId: ThreadId,
    session: OrchestrationSession,
});
export const ThreadActivityAppendedPayload = Schema.Struct({
    threadId: ThreadId,
    activity: OrchestrationThreadActivity,
});
export const OrchestrationEventMetadata = Schema.Struct({
    providerTurnId: Schema.optional(TrimmedNonEmptyString),
    providerItemId: Schema.optional(ProviderItemId),
    adapterKey: Schema.optional(TrimmedNonEmptyString),
    ingestedAt: Schema.optional(IsoDateTime),
});
const EventBaseFields = {
    sequence: NonNegativeInt,
    eventId: EventId,
    aggregateKind: OrchestrationAggregateKind,
    aggregateId: Schema.Union([ProjectId, ThreadId]),
    occurredAt: IsoDateTime,
    commandId: Schema.NullOr(CommandId),
    causationEventId: Schema.NullOr(EventId),
    correlationId: Schema.NullOr(CommandId),
    metadata: OrchestrationEventMetadata,
};
const orchestrationEvent = (type, payload) => Schema.Struct({ ...EventBaseFields, type: Schema.Literal(type), payload });
const ClientProjectCreatedPayload = ProjectCreatedPayload.mapFields(Struct.omit(["defaultModelSelection"]));
const ClientProjectMetaUpdatedPayload = ProjectMetaUpdatedPayload.mapFields(Struct.omit(["defaultModelSelection"]));
const ClientThreadCreatedPayload = ThreadCreatedPayload.mapFields(Struct.omit(["modelSelection"]));
const ClientThreadMetaUpdatedPayload = ThreadMetaUpdatedPayload.mapFields(Struct.omit(["modelSelection"]));
const ClientThreadTurnStartRequestedPayload = ThreadTurnStartRequestedPayload.mapFields(Struct.omit(["modelSelection", "authorUserId"]));
const ClientThreadSessionSetPayload = ThreadSessionSetPayload.mapFields(Struct.assign({ session: ClientOrchestrationSession }));
const ClientThreadActivityAppendedPayload = ThreadActivityAppendedPayload.mapFields(Struct.assign({ activity: ClientOrchestrationThreadActivity }));
export const OrchestrationEvent = Schema.Union([
    orchestrationEvent("project.created", ProjectCreatedPayload),
    orchestrationEvent("project.meta-updated", ProjectMetaUpdatedPayload),
    orchestrationEvent("project.deleted", ProjectDeletedPayload),
    orchestrationEvent("project.archived", ProjectArchivedPayload),
    orchestrationEvent("project.unarchived", ProjectUnarchivedPayload),
    orchestrationEvent("thread.created", ThreadCreatedPayload),
    orchestrationEvent("thread.deleted", ThreadDeletedPayload),
    orchestrationEvent("thread.archived", ThreadArchivedPayload),
    orchestrationEvent("thread.unarchived", ThreadUnarchivedPayload),
    orchestrationEvent("thread.meta-updated", ThreadMetaUpdatedPayload),
    orchestrationEvent("thread.runtime-mode-set", LegacyThreadEventPayload),
    orchestrationEvent("thread.interaction-mode-set", LegacyThreadEventPayload),
    orchestrationEvent("thread.message-sent", ThreadMessageSentPayload),
    orchestrationEvent("thread.turn-start-requested", ThreadTurnStartRequestedPayload),
    orchestrationEvent("thread.turn-interrupt-requested", ThreadTurnInterruptRequestedPayload),
    orchestrationEvent("thread.turn-settled", ThreadTurnSettledPayload),
    orchestrationEvent("thread.approval-response-requested", LegacyThreadEventPayload),
    orchestrationEvent("thread.user-input-response-requested", LegacyThreadEventPayload),
    orchestrationEvent("thread.session-stop-requested", ThreadSessionStopRequestedPayload),
    orchestrationEvent("thread.session-set", ThreadSessionSetPayload),
    orchestrationEvent("thread.proposed-plan-upserted", LegacyThreadEventPayload),
    orchestrationEvent("thread.activity-appended", ThreadActivityAppendedPayload),
]);
export const ClientOrchestrationEvent = Schema.Union([
    orchestrationEvent("project.created", ClientProjectCreatedPayload),
    orchestrationEvent("project.meta-updated", ClientProjectMetaUpdatedPayload),
    orchestrationEvent("project.deleted", ProjectDeletedPayload),
    orchestrationEvent("project.archived", ProjectArchivedPayload),
    orchestrationEvent("project.unarchived", ProjectUnarchivedPayload),
    orchestrationEvent("thread.created", ClientThreadCreatedPayload),
    orchestrationEvent("thread.deleted", ThreadDeletedPayload),
    orchestrationEvent("thread.archived", ThreadArchivedPayload),
    orchestrationEvent("thread.unarchived", ThreadUnarchivedPayload),
    orchestrationEvent("thread.meta-updated", ClientThreadMetaUpdatedPayload),
    orchestrationEvent("thread.runtime-mode-set", LegacyThreadEventPayload),
    orchestrationEvent("thread.interaction-mode-set", LegacyThreadEventPayload),
    orchestrationEvent("thread.message-sent", ThreadMessageSentPayload),
    orchestrationEvent("thread.turn-start-requested", ClientThreadTurnStartRequestedPayload),
    orchestrationEvent("thread.turn-interrupt-requested", ThreadTurnInterruptRequestedPayload),
    orchestrationEvent("thread.turn-settled", ThreadTurnSettledPayload),
    orchestrationEvent("thread.approval-response-requested", LegacyThreadEventPayload),
    orchestrationEvent("thread.user-input-response-requested", LegacyThreadEventPayload),
    orchestrationEvent("thread.session-stop-requested", ThreadSessionStopRequestedPayload),
    orchestrationEvent("thread.session-set", ClientThreadSessionSetPayload),
    orchestrationEvent("thread.proposed-plan-upserted", LegacyThreadEventPayload),
    orchestrationEvent("thread.activity-appended", ClientThreadActivityAppendedPayload),
]);
export const ClientOrchestrationThreadStreamItem = Schema.Union([
    Schema.Struct({
        kind: Schema.Literal("rail"),
        update: UserMessageRailUpdate,
    }),
    Schema.Struct({
        kind: Schema.Literal("snapshot"),
        snapshot: ClientOrchestrationThreadDetailSnapshot,
    }),
    Schema.Struct({
        kind: Schema.Literal("event"),
        event: ClientOrchestrationEvent,
    }),
    Schema.Struct({
        kind: Schema.Literal("live"),
        live: OrchestrationLiveMessageFrame,
    }),
]);
export const OrchestrationCommandReceiptStatus = Schema.Literals(["accepted", "rejected"]);
export const ProviderSessionRuntimeStatus = Schema.Literals([
    "starting",
    "running",
    "stopped",
    "error",
]);
const ProjectionThreadTurnStatus = Schema.Literals([
    "running",
    "completed",
    "interrupted",
    "error",
]);
export const DispatchResult = Schema.Struct({
    sequence: NonNegativeInt,
});
export const OrchestrationReplayEventsInput = Schema.Struct({
    fromSequenceExclusive: NonNegativeInt,
});
const OrchestrationReplayEventsResult = Schema.Array(ClientOrchestrationEvent);
export const OrchestrationFetchOlderThreadMessagesInput = Schema.Struct({
    threadId: ThreadId,
    beforeCursor: MessageCursor,
    limit: NonNegativeInt,
});
export const OrchestrationFetchOlderThreadMessagesResult = Schema.Struct({
    messages: Schema.Array(OrchestrationMessage),
    hasMoreBefore: Schema.Boolean,
    oldestCursor: Schema.NullOr(MessageCursor),
});
export const OrchestrationFetchOlderThreadActivitiesInput = Schema.Struct({
    threadId: ThreadId,
    beforeCursor: ActivityCursor,
    limit: NonNegativeInt,
});
export const OrchestrationFetchOlderThreadActivitiesResult = Schema.Struct({
    activities: Schema.Array(ClientOrchestrationThreadActivity),
    hasMoreBefore: Schema.Boolean,
    oldestCursor: Schema.NullOr(ActivityCursor),
});
export const OrchestrationExportThreadDebugLogInput = Schema.Struct({
    threadId: ThreadId,
});
export const OrchestrationExportThreadDebugLogResult = Schema.Struct({
    fileName: Schema.String,
    content: Schema.String,
});
export const OrchestrationRpcSchemas = {
    dispatchCommand: {
        input: ClientOrchestrationCommand,
        output: DispatchResult,
    },
    replayEvents: {
        input: OrchestrationReplayEventsInput,
        output: OrchestrationReplayEventsResult,
    },
    getArchivedShellSnapshot: {
        input: Schema.Struct({}),
        output: ClientOrchestrationShellSnapshot,
    },
    subscribeThread: {
        input: OrchestrationSubscribeThreadInput,
        output: ClientOrchestrationThreadStreamItem,
    },
    subscribeShell: {
        input: Schema.Struct({ afterSequence: Schema.optional(NonNegativeInt) }),
        output: ClientOrchestrationShellStreamItem,
    },
    fetchOlderThreadMessages: {
        input: OrchestrationFetchOlderThreadMessagesInput,
        output: OrchestrationFetchOlderThreadMessagesResult,
    },
    fetchOlderThreadActivities: {
        input: OrchestrationFetchOlderThreadActivitiesInput,
        output: OrchestrationFetchOlderThreadActivitiesResult,
    },
    exportThreadDebugLog: {
        input: OrchestrationExportThreadDebugLogInput,
        output: OrchestrationExportThreadDebugLogResult,
    },
};
export class OrchestrationGetSnapshotError extends Schema.TaggedError()("OrchestrationGetSnapshotError", {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
}) {
}
export class OrchestrationDispatchCommandError extends Schema.TaggedError()("OrchestrationDispatchCommandError", {
    message: TrimmedNonEmptyString,
    reason: Schema.optional(Schema.Literals([
        "project_owner_required",
        "project_files_delete_unavailable",
        "project_directory_unsafe",
    ])),
    ownerName: Schema.optional(Schema.NullOr(Schema.String)),
    cause: Schema.optional(Schema.Defect()),
}, { httpApiStatus: 403 }) {
}
export class OrchestrationReplayEventsError extends Schema.TaggedError()("OrchestrationReplayEventsError", {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
}) {
}
